import {
    BanknoteIcon,
    BriefcaseIcon,
    Building2Icon,
    CameraIcon,
    CarIcon,
    CloudRainIcon,
    CoinsIcon,
    CreditCardIcon,
    CrossIcon,
    Gamepad2Icon,
    GemIcon,
    GiftIcon,
    GlobeIcon,
    GraduationCapIcon,
    HomeIcon,
    LandmarkIcon,
    PhoneIcon,
    PiggyBankIcon,
    SmartphoneIcon,
    SparklesIcon,
    TrophyIcon,
    TvIcon,
    VaultIcon,
    WalletIcon,
} from 'lucide-react-native';

import { accent } from '../../themes/colors/palettes/accent';
import { gray } from '../../themes/colors/palettes/gray';
import { leaf } from '../../themes/colors/palettes/leaf';
import { lemon } from '../../themes/colors/palettes/lemon';
import { peach } from '../../themes/colors/palettes/peach';
import { petal } from '../../themes/colors/palettes/petal';
import { sweetViolet } from '../../themes/colors/palettes/sweetViolet';
import { water } from '../../themes/colors/palettes/water';
import FruitfulLogoIcon from './custom/FruitfulLogo.svg';
import type { IconType } from './Icon';

// Mapping from readable name to internal config name.
// This means what is saved in synctera for an account isn't directly dependent on our internal naming.
// This should be kept consistent with the backend and web frontend.

export const SPACE_ICONS: Record<string, IconType> = {
    fruitful: FruitfulLogoIcon,
    bankNote: BanknoteIcon,
    briefCase: BriefcaseIcon,
    building: Building2Icon,
    bank: LandmarkIcon,
    camera: CameraIcon,
    cloudRaining: CloudRainIcon,
    coinsStacked: CoinsIcon,
    creditCard: CreditCardIcon,
    cryptocurrency: VaultIcon,
    diamond: GemIcon,
    gamingPad: Gamepad2Icon,
    gift: GiftIcon,
    globe: GlobeIcon,
    graduationHat: GraduationCapIcon,
    home: HomeIcon,
    medicalCross: CrossIcon,
    telephone: PhoneIcon,
    cellphone: SmartphoneIcon,
    piggyBank: PiggyBankIcon,
    stars: SparklesIcon,
    car: CarIcon,
    trophy: TrophyIcon,
    tv: TvIcon,
    wallet: WalletIcon,
};

export const SPACE_COLOR_KEYS = {
    GREEN: 'green',
    RED: 'red',
    ORANGE: 'orange',
    PEACH: 'peach',
    BLUE: 'blue',
    PURPLE: 'purple',
    GREENLEAF: 'greenleaf',
} as const;

/**
 * CSS color strings (hex). These feed both Tamagui `backgroundColor` and
 * Icon `stringColor` / SVG fills — so they must be resolved hex, not `$token`s.
 */
type CssColor = string;

export const SPACE_COLORS: Record<string, CssColor> = {
    [SPACE_COLOR_KEYS.GREEN]: leaf.leaf,
    [SPACE_COLOR_KEYS.RED]: petal.petal,
    [SPACE_COLOR_KEYS.ORANGE]: lemon.lemon,
    [SPACE_COLOR_KEYS.PEACH]: peach.peach,
    [SPACE_COLOR_KEYS.BLUE]: water.water,
    [SPACE_COLOR_KEYS.PURPLE]: sweetViolet.sweetViolet700,
    [SPACE_COLOR_KEYS.GREENLEAF]: gray.gray100, // TODO: remove once no longer referenced by synctera accts
};

export const SPACE_SECONDARY_COLORS: Record<string, CssColor> = {
    [SPACE_COLOR_KEYS.GREEN]: leaf.leafDarkest,
    [SPACE_COLOR_KEYS.RED]: petal.petalDarkest,
    [SPACE_COLOR_KEYS.ORANGE]: lemon.lemonDarkest,
    [SPACE_COLOR_KEYS.PEACH]: peach.peachDarkest,
    [SPACE_COLOR_KEYS.BLUE]: water.waterDarkest,
    [SPACE_COLOR_KEYS.PURPLE]: sweetViolet.sweetViolet1200,
    [SPACE_COLOR_KEYS.GREENLEAF]: accent.black, // TODO: remove once no longer referenced by synctera accts
};

export const SPACE_TERTIARY_COLORS: Record<string, CssColor> = {
    [SPACE_COLOR_KEYS.GREEN]: leaf.leafLightest,
    [SPACE_COLOR_KEYS.RED]: petal.petalLightest,
    [SPACE_COLOR_KEYS.ORANGE]: lemon.lemonLightest,
    [SPACE_COLOR_KEYS.PEACH]: peach.peachLightest,
    [SPACE_COLOR_KEYS.BLUE]: water.waterLightest,
    [SPACE_COLOR_KEYS.PURPLE]: sweetViolet.sweetViolet100,
    [SPACE_COLOR_KEYS.GREENLEAF]: gray.gray100, // TODO: remove once no longer referenced by synctera accts
};

export const ALT_COLORS: Record<string, CssColor> = {
    [SPACE_COLOR_KEYS.GREEN]: gray.gray100,
    [SPACE_COLOR_KEYS.RED]: gray.gray100,
    [SPACE_COLOR_KEYS.ORANGE]: gray.gray100,
    [SPACE_COLOR_KEYS.PEACH]: gray.gray100,
    [SPACE_COLOR_KEYS.BLUE]: gray.gray100,
    [SPACE_COLOR_KEYS.PURPLE]: gray.gray100,
};

export const ALT_SECONDARY_COLORS: Record<string, CssColor> = {
    [SPACE_COLOR_KEYS.GREEN]: leaf.leafDark,
    [SPACE_COLOR_KEYS.RED]: petal.petalDark,
    [SPACE_COLOR_KEYS.ORANGE]: lemon.lemonDark,
    [SPACE_COLOR_KEYS.PEACH]: peach.peachDark,
    [SPACE_COLOR_KEYS.BLUE]: water.waterDark,
    [SPACE_COLOR_KEYS.PURPLE]: sweetViolet.sweetViolet1100,
};

export const DEFAULT_ICON = 'bankNote';
export const DEFAULT_COLOR = 'green';
export const FRUITFUL_ICON_KEY = 'fruitful';
